import { API_BASE_URL } from "@kupikupi/brand";
import { useInfiniteQuery } from "@tanstack/react-query";
import { listProductHotSale } from "../endpoints/product";
import { useBackendLang } from "../lang-context";
import type { ProductHotSaleInput } from "../path-adapter";
import { withSearchTiming } from "../search-timing";

type UseHotSaleInfiniteInput = Omit<ProductHotSaleInput, "pageNo">;

/**
 * Infinite-scroll variant of /product/hot/sale/list — for catalog with no
 * keyword (channel-only browse). Hydrates from SSR prefetch when present.
 */
export function useHotSaleInfinite(input: UseHotSaleInfiniteInput) {
  const lang = useBackendLang();
  return useInfiniteQuery({
    // `lang` last; SSR prefetch in catalog/page.tsx mirrors this (lockstep).
    queryKey: [
      "product",
      "hot-sale-infinite",
      input.channel,
      input.categoryId,
      input.keyword,
      input.pageSize,
      input.sort,
      input.priceStart,
      input.priceEnd,
      lang,
    ],
    queryFn: ({ pageParam = 1 }) =>
      withSearchTiming(
        {
          kind: "hotsale",
          channel: input.channel,
          keywordLen: input.keyword?.length ?? 0,
          pageNo: pageParam as number,
        },
        () =>
          listProductHotSale(
            { baseUrl: API_BASE_URL },
            { ...input, pageNo: pageParam as number },
            {
              lang,
            },
          ),
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.current < lastPage.pages) return lastPage.current + 1;
      return undefined;
    },
    staleTime: 60 * 1000,
  });
}
